import { DONE_NODE_ID, START_NODE_ID, UNASSIGNED_PHASE_ID } from './types';
import type { NodeId, Point } from './types';

/**
 * The builder's geometry, as pure arithmetic (research D3, D14).
 *
 * No React and no canvas library, because this is the one part of the feature
 * jsdom cannot verify by rendering: a headless DOM has no layout, so a test that
 * asserts "the node moved into the next band" through the component would be
 * asserting nothing. Kept here, exported, and unit-tested directly — the same
 * split the board made for its drag handlers, for the same reason.
 *
 * **Band extents are derived, never stored.** A band is as wide as its contents
 * plus padding. The service stores per-node offsets and no band geometry at all,
 * so deriving is what keeps the two sides from disagreeing about a rectangle
 * neither of them owns.
 */

// --- Dimensions, shared with the graph view model ---------------------------
//
// Node sizes and the accent scheme are unchanged from the read-only graph. Only
// their placement is this feature's business.

export const STEP_NODE_WIDTH = 260;
export const STEP_NODE_HEIGHT = 132;
export const SYSTEM_NODE_WIDTH = 128;
export const SYSTEM_NODE_HEIGHT = 48;
export const NODE_GAP = 72;
export const PHASE_PAD = 20;
/** The band's title strip, above the steps it encloses. */
export const LANE_HEADER_HEIGHT = 34;
/** How many distinct phase accents the token layer defines. */
export const PHASE_ACCENT_COUNT = 4;

/** Top edge of the step area — leaves room for the band's header strip. */
export const ROW_TOP = LANE_HEADER_HEIGHT + 28;
export const CANVAS_PAD = 24;

/**
 * The narrowest a band may be (FR-042).
 *
 * An empty phase still has to be a drop target and still has to show its name,
 * so it cannot collapse to nothing.
 */
export const MIN_BAND_WIDTH = STEP_NODE_WIDTH + PHASE_PAD * 2;

/** Vertical stride between stacked steps inside one band. */
export const ROW_STRIDE = STEP_NODE_HEIGHT + NODE_GAP / 2;

// --- Inputs ------------------------------------------------------------------

/**
 * The minimum both a `WorkflowDraft` and a `WorkflowView` satisfy.
 *
 * Typed against this rather than against the draft on purpose: the read-only
 * graph page has no draft, and `workflowToGraph` receives a view. One geometry
 * module serving both is what stops the two canvases from drifting apart
 * (FR-057).
 */
export type LayoutInput = {
  phases: ReadonlyArray<{
    id: string;
    name: string;
    steps: ReadonlyArray<{ id: string }>;
  }>;
  layout: Readonly<Record<NodeId, Point>>;
};

export type BandKind = 'start' | 'phase' | 'unassigned' | 'done';

export type Band = {
  kind: BandKind;
  /** Phase id, or null for the three implicit regions. */
  id: string | null;
  name: string;
  /** Absolute canvas coordinates of the band's top-left corner. */
  origin: Point;
  width: number;
  height: number;
  accentIndex: number;
};

export type BandLayout = {
  bands: Band[];
  width: number;
  height: number;
};

// --- Bands -------------------------------------------------------------------

/**
 * Band origins and extents, left to right: Backlog · phases in order ·
 * Unassigned · Done.
 *
 * The Unassigned band renders only when it holds something (FR-041) — an empty
 * holding pen is visual noise, and its absence is the normal state.
 *
 * `frozenWidths` pins named bands to a width instead of deriving one, and exists
 * for the duration of a node drag. Widths are derived from members' offsets, so
 * moving a node grows its band on every pointer frame — which shifts every band
 * to its right. Dragging a node *toward* the phase on its right therefore pushed
 * that phase away at the speed of the drag, and it could never be reached. The
 * geometry a user is aiming at has to hold still while they aim.
 */
export function bandLayout(
  input: LayoutInput,
  frozenWidths?: Readonly<Record<string, number>> | null,
): BandLayout {
  const phases = input.phases.filter(
    (phase) => phase.id !== UNASSIGNED_PHASE_ID,
  );
  const unassigned = input.phases.find(
    (phase) => phase.id === UNASSIGNED_PHASE_ID,
  );

  const bands: Band[] = [];
  let x = CANVAS_PAD;

  const push = (
    kind: BandKind,
    id: string | null,
    name: string,
    width: number,
    accentIndex: number,
  ): void => {
    bands.push({
      kind,
      id,
      name,
      origin: { x, y: 0 },
      width,
      // Height is filled in below, once every band's content is known: bands run
      // floor to ceiling, so they all share one height.
      height: 0,
      accentIndex,
    });
    x += width + NODE_GAP;
  };

  push('start', START_NODE_ID, 'Backlog', SYSTEM_NODE_WIDTH + PHASE_PAD * 2, 0);

  const widthOf = (phase: {
    id: string;
    steps: ReadonlyArray<{ id: string }>;
  }) => frozenWidths?.[phase.id] ?? bandWidthFor(phase, input.layout);

  phases.forEach((phase, index) => {
    push(
      'phase',
      phase.id,
      phase.name,
      widthOf(phase),
      index % PHASE_ACCENT_COUNT,
    );
  });

  if (unassigned && unassigned.steps.length > 0) {
    push(
      'unassigned',
      UNASSIGNED_PHASE_ID,
      'Unassigned',
      widthOf(unassigned),
      // Deliberately outside the phase accent rotation: the holding pen is not
      // a stage of the process and must not read as one.
      PHASE_ACCENT_COUNT,
    );
  }

  push('done', DONE_NODE_ID, 'Done', SYSTEM_NODE_WIDTH + PHASE_PAD * 2, 0);

  const height = canvasHeight(input);
  for (const band of bands) band.height = height;

  return {
    bands,
    // `x` has already advanced past the final band by one gap; trade it for the
    // canvas's right-hand padding.
    width: x - NODE_GAP + CANVAS_PAD,
    height,
  };
}

/** A band is as wide as its widest member's right edge, floored at the minimum. */
function bandWidthFor(
  phase: { steps: ReadonlyArray<{ id: string }> },
  layout: Readonly<Record<NodeId, Point>>,
): number {
  let widest = 0;
  for (const step of phase.steps) {
    const offset = layout[step.id];
    const right = (offset?.x ?? 0) + STEP_NODE_WIDTH;
    if (right > widest) widest = right;
  }
  return Math.max(MIN_BAND_WIDTH, widest + PHASE_PAD * 2);
}

/**
 * One height for every band, computed from the deepest stack on the canvas.
 *
 * A constant in the read-only version, because every workflow was one row. With
 * branching a phase can hold several steps at the same depth, so the canvas has
 * to grow downwards.
 */
function canvasHeight(input: LayoutInput): number {
  let lowest = 0;
  for (const phase of input.phases) {
    for (const step of phase.steps) {
      const bottom = (input.layout[step.id]?.y ?? 0) + STEP_NODE_HEIGHT;
      if (bottom > lowest) lowest = bottom;
    }
  }
  return ROW_TOP + Math.max(STEP_NODE_HEIGHT, lowest) + CANVAS_PAD;
}

// --- Coordinate conversion ---------------------------------------------------

/**
 * Band-relative offset → absolute canvas position.
 *
 * A node whose band no longer exists returns `null` rather than landing at the
 * origin: silently stacking orphans at (0, 0) looks like a rendering bug, while
 * omitting them is visible and recoverable.
 */
export function toCanvas(
  nodeId: NodeId,
  bandId: string | null,
  layout: Readonly<Record<NodeId, Point>>,
  bands: readonly Band[],
): Point | null {
  const band = bands.find((candidate) => candidate.id === bandId);
  if (!band) return null;

  const offset = layout[nodeId] ?? { x: 0, y: 0 };
  return {
    x: band.origin.x + PHASE_PAD + offset.x,
    y: band.origin.y + ROW_TOP + offset.y,
  };
}

/**
 * Absolute canvas point → the band under it and the offset within it.
 *
 * The basis of drop-to-assign (FR-039) and of dropping from the palette: where
 * a node lands is what decides which phase owns it, so this is the only place
 * that decision is made.
 *
 * A point past the last band or before the first clamps to the nearest, because
 * a drop that lands in a gutter should still do something predictable.
 *
 * The offset is floored at zero on both axes. A drop inside the band's own
 * padding — or in a gutter, which `nearestBand` resolves to a band the point is
 * not actually over — otherwise yields a negative offset, and a negative offset
 * draws the node outside the band that owns it: left of its edge, or up inside
 * the header. `bandWidthFor` only measures right edges, so nothing downstream
 * grows the band to cover it. FR-042 requires a band to *contain* its members,
 * and containment is cheaper to guarantee here, at the one place a point becomes
 * an offset, than to repair everywhere a band is measured.
 */
export function fromCanvas(
  point: Point,
  bands: readonly Band[],
): { band: Band; offset: Point } | null {
  if (bands.length === 0) return null;

  const hit =
    bands.find(
      (band) =>
        point.x >= band.origin.x && point.x < band.origin.x + band.width,
    ) ?? nearestBand(point, bands);

  const raw = offsetWithin(hit, point);
  return {
    band: hit,
    offset: { x: Math.max(0, raw.x), y: Math.max(0, raw.y) },
  };
}

/**
 * A point in one named band's frame, *unclamped*.
 *
 * The raw arithmetic behind `fromCanvas`, exported because a drag in progress
 * needs it without the flooring. While the pointer is down the node has to sit
 * exactly where the pointer is, including left of its band — clamping mid-drag
 * makes the card stop dead while the cursor carries on, which reads as the card
 * having come unstuck. Containment is a property of where a node *lands*, so
 * `fromCanvas` floors and this does not.
 */
export function offsetWithin(band: Band, point: Point): Point {
  return {
    x: point.x - band.origin.x - PHASE_PAD,
    y: point.y - band.origin.y - ROW_TOP,
  };
}

function nearestBand(point: Point, bands: readonly Band[]): Band {
  let best = bands[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const band of bands) {
    const centre = band.origin.x + band.width / 2;
    const distance = Math.abs(point.x - centre);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = band;
    }
  }
  return best;
}

// --- Auto-layout -------------------------------------------------------------

/**
 * A starting arrangement for a workflow that has none (research D4, FR-014).
 *
 * Column by BFS depth from the start step, row by order of first appearance at
 * that depth *within its phase*. First visit wins, which is what terminates
 * cycles — a revision loop must not send this into an infinite descent.
 *
 * Runs once, on first open of an imported workflow, and produces something the
 * user immediately drags around. That is why it is sixty lines of BFS rather
 * than a layout library: hierarchical edge-crossing minimisation would be a
 * large dependency inside an already-measured lazy chunk, spent on a picture
 * that is about to be rearranged by hand.
 *
 * **Determinism is the property that matters** and is asserted directly: the
 * same workflow must produce the same arrangement every time, or the read-only
 * graph and the builder would disagree about an un-arranged workflow (FR-057).
 */
export type AutoLayoutInput = {
  phases: ReadonlyArray<{
    id: string;
    steps: ReadonlyArray<{
      id: string;
      transitions?: ReadonlyArray<{ to: string }>;
    }>;
  }>;
  start?: string | null;
};

export function autoLayout(input: AutoLayoutInput): Record<NodeId, Point> {
  const phaseOf = new Map<string, string>();
  const stepsById = new Map<
    string,
    { id: string; transitions?: ReadonlyArray<{ to: string }> }
  >();
  const declarationOrder: string[] = [];

  for (const phase of input.phases) {
    for (const step of phase.steps) {
      phaseOf.set(step.id, phase.id);
      stepsById.set(step.id, step);
      declarationOrder.push(step.id);
    }
  }

  const depth = new Map<string, number>();
  const roots = entryPoints(input, stepsById, declarationOrder);

  // Breadth-first so that depth is assigned in non-decreasing order and the
  // first visit is always the shallowest one.
  const queue: string[] = [];
  for (const root of roots) {
    if (!depth.has(root)) {
      depth.set(root, 0);
      queue.push(root);
    }
  }

  while (queue.length > 0) {
    const id = queue.shift() as string;
    const current = depth.get(id) ?? 0;
    for (const transition of stepsById.get(id)?.transitions ?? []) {
      const next = transition.to;
      if (!stepsById.has(next) || depth.has(next)) continue;
      depth.set(next, current + 1);
      queue.push(next);
    }
  }

  // Anything unreachable still needs a position — a draft is allowed to hold
  // orphans, and refusing to draw one would make it uneditable.
  for (const id of declarationOrder) {
    if (!depth.has(id)) depth.set(id, 0);
  }

  // Depth is global — it has to be, or a step's column would depend on which
  // phase it happened to land in rather than on how far into the process it is.
  // But offsets are *band-relative*, so using global depth directly would leave
  // a later phase's band mostly empty gutter: a single step three hops in would
  // sit ~1000px from its own band's left edge, and the band would grow to fit
  // that emptiness.
  //
  // So depth is ranked within each band. Order is preserved — a deeper step is
  // still further right than a shallower one in the same phase — while each band
  // starts its columns at zero and stays as narrow as its contents.
  const columnOf = rankDepthsPerBand(declarationOrder, phaseOf, depth);

  // Row within a band: order of first appearance in each column, in declaration
  // order. Iterating `declarationOrder` rather than a Map's insertion order is
  // what makes this reproducible.
  const rowCursor = new Map<string, number>();
  const layout: Record<NodeId, Point> = {};

  for (const id of declarationOrder) {
    const bandId = phaseOf.get(id) ?? '';
    const column = columnOf.get(id) ?? 0;
    const key = `${bandId}:${column}`;
    const row = rowCursor.get(key) ?? 0;
    rowCursor.set(key, row + 1);

    layout[id] = {
      x: column * (STEP_NODE_WIDTH + NODE_GAP),
      y: row * ROW_STRIDE,
    };
  }

  return layout;
}

/**
 * Global depths → per-band column indices, preserving order within each band.
 *
 * The distinct depths present in a band are sorted and replaced by their rank,
 * so depths 4 and 7 in one phase become columns 0 and 1.
 */
function rankDepthsPerBand(
  declarationOrder: readonly string[],
  phaseOf: ReadonlyMap<string, string>,
  depth: ReadonlyMap<string, number>,
): Map<string, number> {
  const depthsByBand = new Map<string, Set<number>>();
  for (const id of declarationOrder) {
    const bandId = phaseOf.get(id) ?? '';
    const seen = depthsByBand.get(bandId) ?? new Set<number>();
    seen.add(depth.get(id) ?? 0);
    depthsByBand.set(bandId, seen);
  }

  const rankByBand = new Map<string, Map<number, number>>();
  for (const [bandId, depths] of depthsByBand) {
    const ranks = new Map<number, number>();
    [...depths]
      .sort((a, b) => a - b)
      .forEach((value, index) => ranks.set(value, index));
    rankByBand.set(bandId, ranks);
  }

  const columnOf = new Map<string, number>();
  for (const id of declarationOrder) {
    const bandId = phaseOf.get(id) ?? '';
    const rank = rankByBand.get(bandId)?.get(depth.get(id) ?? 0) ?? 0;
    columnOf.set(id, rank);
  }
  return columnOf;
}

/**
 * Where the walk begins: the declared start if it names a real step, otherwise
 * every step nothing targets, otherwise the first declared step.
 *
 * The final fallback exists for a fully cyclic workflow with no declared start —
 * which the service refuses to publish but a draft may hold, and which must
 * still draw.
 */
function entryPoints(
  input: AutoLayoutInput,
  stepsById: ReadonlyMap<string, unknown>,
  declarationOrder: readonly string[],
): string[] {
  if (input.start && stepsById.has(input.start)) return [input.start];

  const targeted = new Set<string>();
  for (const phase of input.phases) {
    for (const step of phase.steps) {
      for (const transition of step.transitions ?? [])
        targeted.add(transition.to);
    }
  }

  const untargeted = declarationOrder.filter((id) => !targeted.has(id));
  if (untargeted.length > 0) return untargeted;

  return declarationOrder.length > 0 ? [declarationOrder[0]] : [];
}

// --- Display order -----------------------------------------------------------

/**
 * Step ids in the order the canvas reads, left to right (FR-017).
 *
 * This is what becomes document order on save, and therefore what the board's
 * column contents follow. Ties break by y then id so that two steps at the same
 * x produce a stable order rather than one that depends on object iteration.
 */
export function stepDisplayOrder(
  steps: ReadonlyArray<{ id: string }>,
  layout: Readonly<Record<NodeId, Point>>,
): string[] {
  return steps
    .map((step) => ({ id: step.id, point: layout[step.id] ?? { x: 0, y: 0 } }))
    .sort((a, b) => {
      if (a.point.x !== b.point.x) return a.point.x - b.point.x;
      if (a.point.y !== b.point.y) return a.point.y - b.point.y;
      return a.id.localeCompare(b.id);
    })
    .map((entry) => entry.id);
}

/**
 * Which phase owns a canvas point, or `null` when no phase does.
 *
 * `null` covers three cases that used to be conflated into `$unassigned`: no
 * bands at all, a drop outside every band, and a drop on Backlog or Done —
 * system nodes a step cannot live inside.
 *
 * Answering with the reserved id was the bug behind "dragging a step onto an
 * empty canvas still creates Unassigned". It reads as an answer, so
 * `addStep` never saw the `undefined` its create-a-phase fallback keys on,
 * while the palette *button* — which passes no phase at all — had already been
 * fixed. Same intent, two paths, one of them still routing to a concept the
 * builder no longer has.
 */
export function phaseAt(point: Point, bands: readonly Band[]): string | null {
  const landed = fromCanvas(point, bands);
  return landed ? phaseOfBand(landed.band) : null;
}

/**
 * The phase a band represents, or `null` if it represents none.
 *
 * Split out so a caller that has already resolved a band — `BuilderCanvas`'s
 * `locate`, which needs the offset as well — can ask the same question without
 * scanning the bands a second time. `phaseAt` and `locate` would otherwise
 * disagree the moment one of them was edited.
 */
export function phaseOfBand(band: Band): string | null {
  if (band.kind !== 'phase' && band.kind !== 'unassigned') return null;
  return band.id ?? null;
}
